import { branding, buildWhatsAppUrl } from '@fsd/shared';
import { WhatsAppIcon } from '@fsd/ui';
import { MotionConfig, motion, useReducedMotion } from 'framer-motion';
import Lenis from 'lenis';
import {
  ArrowRight,
  BadgeCheck,
  CalendarDays,
  Check,
  ChevronDown,
  CircleDollarSign,
  ClipboardCheck,
  CreditCard,
  GraduationCap,
  Map as MapIcon,
  RadioTower,
  ShieldCheck,
  Sprout,
  Target,
  Tractor,
} from 'lucide-react';
import { useEffect } from 'react';
import p150FieldA from '@/assets/drones/p150/1.webp';
import p150FieldB from '@/assets/drones/p150/2.webp';
import p150FieldC from '@/assets/drones/p150/3.webp';
import p150FieldD from '@/assets/drones/p150/4.webp';
import p150Hero from '@/assets/drones/p150/5.webp';
import logoWhite from '@/assets/logo/white.svg';
import {
  audienceItems,
  courseSignals,
  importantNotes,
  includedItems,
  instructors,
  investmentLots,
  pilotNavItems,
  practiceModules,
  theoryModules,
} from './content';

const whatsappMessage =
  'Olá, quero saber mais sobre o treinamento Piloto Fronteira, de 24 a 28 de agosto. Pode me enviar detalhes sobre vagas e inscrição?';

const whatsappUrl = buildWhatsAppUrl(branding.contact.phone, whatsappMessage);

const fieldImages = [
  { src: p150FieldA, alt: 'Drone agrícola XAG P150 em solo de campo' },
  { src: p150FieldB, alt: 'Drone agrícola XAG P150 preparado para pulverização' },
  { src: p150FieldC, alt: 'Detalhe operacional do drone agrícola XAG P150' },
  { src: p150FieldD, alt: 'XAG P150 em ambiente de operação agrícola' },
] as const;

const fadeUp = {
  hidden: { opacity: 0, y: 34 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.72, ease: [0.22, 1, 0.36, 1] },
  },
} as const;

const stagger = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.08,
    },
  },
} as const;

function useLenisScroll() {
  const reducedMotion = useReducedMotion();

  useEffect(() => {
    if (reducedMotion) return;

    const lenis = new Lenis({
      anchors: true,
      autoRaf: true,
      lerp: 0.08,
      smoothWheel: true,
      syncTouch: false,
      wheelMultiplier: 0.86,
    });

    return () => {
      lenis.destroy();
    };
  }, [reducedMotion]);
}

function scrollToSection(href: string) {
  const target = document.querySelector(href);
  if (target) {
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    return;
  }

  if (href === '#inicio') {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
}

function CourseJsonLd() {
  useEffect(() => {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'Course',
      name: 'Treinamento Piloto Fronteira',
      description:
        'Curso profissional para piloto de drones agrícolas com teoria CAAR, prática em XAG P150, certificado, material didático e suporte pós-curso.',
      provider: {
        '@type': 'Organization',
        name: branding.brandName,
        url: `https://${branding.primaryDomain}`,
      },
      courseMode: 'InPerson',
      inLanguage: 'pt-BR',
      hasCourseInstance: {
        '@type': 'CourseInstance',
        startDate: '2026-08-24',
        endDate: '2026-08-28',
        courseMode: 'InPerson',
      },
      offers: investmentLots.map((lot) => ({
        '@type': 'Offer',
        category: lot.name,
        priceCurrency: 'BRL',
        price: lot.price.replace(/\D/g, '').replace(/(\d{2})$/, '.$1'),
        availability: 'https://schema.org/InStock',
        url: `https://${branding.primaryDomain}/`,
      })),
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-piloto-fronteira-schema', 'true');
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      script.remove();
    };
  }, []);

  return null;
}

function Eyebrow({ children, dark = false }: { children: string; dark?: boolean }) {
  return (
    <span
      className={`inline-flex items-center gap-2 rounded-md border px-3 py-1 text-xs font-semibold ${
        dark
          ? 'border-white/15 bg-white/8 text-white/78'
          : 'border-emerald-900/10 bg-emerald-950/4 text-emerald-950/70'
      }`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${dark ? 'bg-[#a9d96e]' : 'bg-[#3f7d48]'}`} />
      {children}
    </span>
  );
}

function WhatsAppCta({
  children,
  variant = 'primary',
  desktopOnly = false,
  className = '',
}: {
  children: string;
  variant?: 'primary' | 'ghost';
  desktopOnly?: boolean;
  className?: string;
}) {
  const classes =
    variant === 'primary'
      ? 'bg-[#d8f2a3] text-[#102015] hover:bg-white'
      : 'border border-white/20 bg-white/8 text-white hover:bg-white/14';
  const displayClass = desktopOnly ? 'hidden lg:inline-flex' : 'inline-flex';

  return (
    <motion.a
      href={whatsappUrl}
      target="_blank"
      rel="noopener noreferrer"
      className={`group ${displayClass} min-h-12 items-center justify-center gap-3 rounded-md px-5 py-3 text-sm font-semibold transition duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#d8f2a3] focus-visible:ring-offset-2 focus-visible:ring-offset-[#07100c] ${classes} ${className}`}
      whileHover={{ y: -2 }}
    >
      <WhatsAppIcon className="h-5 w-5" />
      {children}
      <span className="flex h-7 w-7 items-center justify-center rounded-md bg-[#0d1a11]/10 transition-transform duration-300 group-hover:translate-x-1">
        <ArrowRight className="h-4 w-4" />
      </span>
    </motion.a>
  );
}

function PilotNav() {
  return (
    <nav
      className="fixed left-0 right-0 top-5 z-50 hidden px-6 transform-[translate3d(0,0,0)] will-change-transform lg:block"
      aria-label="Principal"
    >
      <div className="mx-auto flex h-14 max-w-6xl items-center justify-between rounded-md border border-white/12 bg-[#07100c]/76 px-3 text-white shadow-[0_24px_80px_rgba(2,9,5,0.24)] backdrop-blur-xl sm:h-16 sm:px-5">
        <button
          type="button"
          className="flex items-center gap-2 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#d8f2a3]"
          onClick={() => scrollToSection('#inicio')}
          aria-label="Voltar ao inicio"
        >
          <img src={logoWhite} alt={branding.brandName} className="h-8 w-auto sm:h-9" />
        </button>

        <div className="hidden items-center gap-1 lg:flex">
          {pilotNavItems.map((item) => (
            <button
              type="button"
              key={item.href}
              onClick={() => scrollToSection(item.href)}
              className="rounded-md px-4 py-2 text-sm font-medium text-white/72 transition duration-300 hover:bg-white/8 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#d8f2a3]"
            >
              {item.label}
            </button>
          ))}
        </div>

        <a
          href={whatsappUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="hidden min-h-10 items-center gap-2 rounded-md bg-white px-3 text-xs font-semibold text-[#102015] transition hover:bg-[#d8f2a3] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#d8f2a3] sm:px-4 sm:text-sm lg:inline-flex"
        >
          <WhatsAppIcon className="h-4 w-4" />
          Inscrição
        </a>
      </div>
    </nav>
  );
}

function HeroSection() {
  const reducedMotion = useReducedMotion();

  return (
    <section
      id="inicio"
      className="relative isolate flex min-h-dvh items-end overflow-hidden bg-[#07100c] text-white"
    >
      <motion.img
        src={p150Hero}
        alt="Drone agrícola XAG P150 em área de campo"
        className="absolute inset-0 h-full w-full object-cover"
        fetchPriority="high"
        decoding="async"
        initial={false}
        animate={reducedMotion ? undefined : { scale: [1.04, 1.09, 1.04] }}
        transition={
          reducedMotion
            ? undefined
            : { duration: 18, repeat: Number.POSITIVE_INFINITY, ease: [0.22, 1, 0.36, 1] }
        }
      />
      <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(7,16,12,0.96)_0%,rgba(7,16,12,0.78)_40%,rgba(7,16,12,0.24)_100%)]" />
      <div className="absolute inset-x-0 bottom-0 h-48 bg-[linear-gradient(180deg,rgba(7,16,12,0),#07100c)]" />

      <div className="relative z-10 mx-auto grid w-full max-w-7xl gap-10 px-4 pb-28 pt-7 sm:px-6 sm:pb-32 sm:pt-10 lg:grid-cols-[minmax(0,1.02fr)_minmax(320px,0.58fr)] lg:px-8 lg:pb-24 lg:pt-28">
        <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-3xl">
          <motion.div
            variants={fadeUp}
            className="mb-10 flex items-center justify-between gap-4 lg:hidden"
          >
            <button
              type="button"
              className="rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#d8f2a3]"
              onClick={() => scrollToSection('#inicio')}
              aria-label="Voltar ao inicio"
            >
              <img
                src={logoWhite}
                alt={branding.brandName}
                className="-my-2 h-16 w-auto drop-shadow-[0_8px_18px_rgba(0,0,0,0.34)]"
              />
            </button>

            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex min-h-10 items-center justify-center gap-2 rounded-md border border-white/18 bg-white/10 px-3 text-xs font-semibold text-white backdrop-blur-md transition hover:bg-white/16 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#d8f2a3]"
            >
              <WhatsAppIcon className="h-4 w-4" />
              Inscrição
            </a>
          </motion.div>

          <motion.div variants={fadeUp}>
            <Eyebrow dark>Treinamento profissional de piloto agrícola</Eyebrow>
          </motion.div>

          <motion.h1
            variants={fadeUp}
            className="fsd-display mt-6 max-w-3xl text-5xl font-semibold leading-[0.96] text-white sm:text-6xl lg:text-7xl"
          >
            Piloto Fronteira
          </motion.h1>

          <motion.p
            variants={fadeUp}
            className="mt-6 max-w-2xl text-lg leading-8 text-white/78 sm:text-xl"
          >
            Formação intensiva para operar drones agrícolas com teoria CAAR, prática real no XAG
            P150 e acompanhamento de instrutores que vivem a rotina do campo.
          </motion.p>

          <motion.div variants={fadeUp} className="mt-8 flex flex-col gap-3 sm:flex-row">
            <WhatsAppCta desktopOnly className="w-full sm:w-auto">
              Garantir minha vaga
            </WhatsAppCta>
            <button
              type="button"
              onClick={() => scrollToSection('#formacao')}
              className="inline-flex min-h-12 w-full items-center justify-center gap-3 rounded-md border border-white/18 bg-white/8 px-5 py-3 text-sm font-semibold text-white transition duration-300 hover:bg-white/14 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white sm:w-auto"
            >
              Ver conteúdo do curso
              <ChevronDown className="h-4 w-4" />
            </button>
          </motion.div>

          <motion.div
            variants={stagger}
            className="mt-10 grid max-w-2xl grid-cols-2 gap-px overflow-hidden rounded-md border border-white/12 bg-white/12 sm:grid-cols-4"
          >
            {courseSignals.map((signal) => (
              <motion.div
                variants={fadeUp}
                key={signal.value}
                className="bg-[#07100c]/64 p-4 backdrop-blur-sm"
              >
                <p className="fsd-display text-xl font-semibold text-[#d8f2a3]">{signal.value}</p>
                <p className="mt-1 text-xs font-semibold text-white/82">{signal.label}</p>
                <p className="mt-2 text-xs text-white/54">{signal.detail}</p>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>

        <motion.aside
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.82, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
          className="self-end rounded-md border border-white/14 bg-[#07100c]/68 p-4 backdrop-blur-md"
          aria-label="Resumo do treinamento"
        >
          <div className="flex items-center justify-between border-b border-white/12 pb-4">
            <div>
              <p className="text-xs font-semibold text-white/56">Janela da próxima turma</p>
              <p className="fsd-display mt-1 text-2xl font-semibold">24 a 28 agosto</p>
            </div>
            <CalendarDays className="h-8 w-8 text-[#d8f2a3]" />
          </div>

          <div className="grid gap-4 py-4">
            {[
              ['Modalidade', 'Teoria + campo'],
              ['Operação', 'XAG P150'],
              ['Conversão', 'Inscrição via WhatsApp'],
            ].map(([label, value]) => (
              <div key={label} className="flex items-center justify-between gap-4">
                <span className="text-sm text-white/52">{label}</span>
                <span className="text-right text-sm font-semibold text-white">{value}</span>
              </div>
            ))}
          </div>

          <div className="rounded-md bg-white/8 p-4">
            <p className="text-sm leading-6 text-white/74">
              Mais do que ensinar a pilotar: formar profissionais capazes de gerir toda a operação
              com eficiência, autonomia e responsabilidade.
            </p>
          </div>
        </motion.aside>
      </div>
    </section>
  );
}

function MarketSection() {
  const items = [
    {
      icon: Tractor,
      title: 'O agro está mudando a rotina de aplicação',
      body: 'A pulverização com drones exige profissionais que entendam equipamento, clima, mapa, calda, segurança e ritmo operacional.',
    },
    {
      icon: RadioTower,
      title: 'Tecnologia sem metodo vira risco',
      body: 'O curso conecta RTK, app, radar, mapeamento e calibração a uma rotina de campo organizada.',
    },
    {
      icon: CircleDollarSign,
      title: 'Existe oportunidade para operador qualificado',
      body: 'O treinamento foi estruturado para quem quer trabalhar como piloto, atuar em propriedades ou abrir uma operação de pulverização.',
    },
  ] as const;

  return (
    <section id="mercado" className="bg-[#07100c] px-4 py-20 text-white sm:px-6 lg:px-8 lg:py-28">
      <div className="mx-auto max-w-7xl">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.35 }}
          variants={fadeUp}
          className="max-w-3xl"
        >
          <Eyebrow dark>Mercado em transformação</Eyebrow>
          <h2 className="fsd-display mt-5 text-4xl font-semibold leading-tight sm:text-5xl lg:text-6xl">
            Não basta levantar voo. O campo cobra precisão, segurança e decisão.
          </h2>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={stagger}
          className="mt-12 grid gap-4 lg:grid-cols-[1.1fr_0.9fr_1fr]"
        >
          {items.map((item) => {
            const Icon = item.icon;
            return (
              <motion.article
                key={item.title}
                variants={fadeUp}
                className="rounded-md border border-white/12 bg-white/5.5 p-6"
              >
                <Icon className="h-7 w-7 text-[#d8f2a3]" />
                <h3 className="mt-7 text-xl font-semibold">{item.title}</h3>
                <p className="mt-4 leading-7 text-white/66">{item.body}</p>
              </motion.article>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}

function AuthoritySection() {
  return (
    <section className="bg-[#eef2ea] px-4 py-20 text-[#102015] sm:px-6 lg:px-8 lg:py-28">
      <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-[0.86fr_1.14fr] lg:items-start">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.35 }}
          variants={fadeUp}
          className="lg:sticky lg:top-28"
        >
          <Eyebrow>Quem vai te ensinar</Eyebrow>
          <h2 className="fsd-display mt-5 text-4xl font-semibold leading-tight sm:text-5xl">
            Instrutores com vivência técnica e rotina real de operação.
          </h2>
          <p className="mt-6 max-w-xl text-lg leading-8 text-[#334337]">
            A Fronteira Sul Drones coloca o aluno em contato com profissionais que estão todos os
            dias no campo, operando equipamentos, ajustando processos e resolvendo problemas de
            produtores.
          </p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.18 }}
          variants={stagger}
          className="grid gap-3"
        >
          {instructors.map((instructor, index) => (
            <motion.article
              variants={fadeUp}
              key={instructor.name}
              className="grid gap-4 rounded-md border border-[#102015]/10 bg-white/64 p-5 sm:grid-cols-[72px_1fr]"
            >
              <div className="flex h-16 w-16 items-center justify-center rounded-md bg-[#102015] text-xl font-semibold text-[#d8f2a3]">
                {String(index + 1).padStart(2, '0')}
              </div>
              <div>
                <p className="text-sm font-semibold text-[#3f7d48]">{instructor.role}</p>
                <h3 className="mt-1 text-2xl font-semibold">{instructor.name}</h3>
                <p className="mt-3 leading-7 text-[#4c5b50]">{instructor.description}</p>
              </div>
            </motion.article>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function FormationSection() {
  return (
    <section
      id="formacao"
      className="bg-[#f8faf7] px-4 py-20 text-[#102015] sm:px-6 lg:px-8 lg:py-28"
    >
      <div className="mx-auto max-w-7xl">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.35 }}
          variants={fadeUp}
          className="max-w-3xl"
        >
          <Eyebrow>Do mapa ao relatório</Eyebrow>
          <h2 className="fsd-display mt-5 text-4xl font-semibold leading-tight sm:text-5xl lg:text-6xl">
            Um treinamento feito para formar operador completo.
          </h2>
          <p className="mt-6 text-lg leading-8 text-[#4c5b50]">
            A estrutura combina fundamentos, legislação, segurança, calibração e prática de
            pulverização com acompanhamento dos instrutores.
          </p>
        </motion.div>

        <div className="mt-12 grid gap-6 lg:grid-cols-2">
          <CurriculumPanel
            icon={GraduationCap}
            title="Teoria - CAAR"
            kicker="Curso obrigatório para habilitação MAPA"
            items={theoryModules}
          />
          <CurriculumPanel
            icon={Tractor}
            title="Pratica - XAG P150"
            kicker="A realidade do piloto em campo"
            items={practiceModules}
          />
        </div>
      </div>
    </section>
  );
}

function CurriculumPanel({
  icon: Icon,
  title,
  kicker,
  items,
}: {
  icon: typeof GraduationCap;
  title: string;
  kicker: string;
  items: readonly string[];
}) {
  return (
    <motion.article
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.18 }}
      variants={fadeUp}
      className="rounded-md border border-[#102015]/10 bg-white p-6 shadow-[0_30px_80px_rgba(16,32,21,0.08)]"
    >
      <div className="flex items-start justify-between gap-4 border-b border-[#102015]/10 pb-6">
        <div>
          <p className="text-sm font-semibold text-[#3f7d48]">{kicker}</p>
          <h3 className="fsd-display mt-2 text-3xl font-semibold">{title}</h3>
        </div>
        <Icon className="h-9 w-9 shrink-0 text-[#3f7d48]" />
      </div>
      <ul className="mt-6 grid gap-3">
        {items.map((item) => (
          <li key={item} className="flex gap-3 text-sm leading-6 text-[#4c5b50]">
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#3f7d48]" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </motion.article>
  );
}

function FieldSection() {
  const steps = [
    {
      icon: MapIcon,
      title: 'Mapear',
      body: 'Criar mapas caminhando com RTK, com imagem de satélite ou com o próprio drone.',
    },
    {
      icon: Target,
      title: 'Configurar',
      body: 'Ajustar entrada, saída, radar, taxa variável e modos de pulverização.',
    },
    {
      icon: ShieldCheck,
      title: 'Operar',
      body: 'Conduzir pulverização manual, personalizada e A/B com controle do processo.',
    },
    {
      icon: ClipboardCheck,
      title: 'Finalizar',
      body: 'Secar tanque, retomar operação, revisar baterias e gerar relatórios de voo.',
    },
  ] as const;

  return (
    <section id="campo" className="bg-[#07100c] px-4 py-20 text-white sm:px-6 lg:px-8 lg:py-28">
      <div className="mx-auto max-w-7xl">
        <div className="grid gap-10 lg:grid-cols-[0.92fr_1.08fr] lg:items-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.35 }}
            variants={fadeUp}
          >
            <Eyebrow dark>Pratica acompanhada</Eyebrow>
            <h2 className="fsd-display mt-5 text-4xl font-semibold leading-tight sm:text-5xl">
              O aluno opera o XAG P150 em uma rotina próxima da lavoura.
            </h2>
            <p className="mt-6 text-lg leading-8 text-white/68">
              A turma é dividida em grupos para acesso direto ao drone. O foco é aprender fazendo:
              conhecer o equipamento, mapear, configurar, pulverizar e encerrar a operação com
              criterio.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.22 }}
            variants={stagger}
            className="grid grid-cols-2 gap-3"
          >
            {fieldImages.map((image, index) => (
              <motion.figure
                variants={fadeUp}
                key={image.src}
                className={`overflow-hidden rounded-md border border-white/12 bg-white/6 ${
                  index === 0 ? 'col-span-2 aspect-16/8' : 'aspect-4/3'
                }`}
              >
                <img
                  src={image.src}
                  alt={image.alt}
                  className="h-full w-full object-cover"
                  loading={index === 0 ? 'eager' : 'lazy'}
                  decoding="async"
                />
              </motion.figure>
            ))}
          </motion.div>
        </div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.18 }}
          variants={stagger}
          className="mt-12 grid gap-4 md:grid-cols-2 lg:grid-cols-4"
        >
          {steps.map((step) => {
            const Icon = step.icon;
            return (
              <motion.article
                key={step.title}
                variants={fadeUp}
                className="rounded-md border border-white/12 bg-white/5.5 p-5"
              >
                <Icon className="h-7 w-7 text-[#d8f2a3]" />
                <h3 className="mt-6 text-xl font-semibold">{step.title}</h3>
                <p className="mt-3 text-sm leading-6 text-white/62">{step.body}</p>
              </motion.article>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}

function AudienceSection() {
  return (
    <section className="bg-[#eef2ea] px-4 py-20 text-[#102015] sm:px-6 lg:px-8 lg:py-28">
      <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-[1fr_0.92fr] lg:items-center">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.35 }}
          variants={fadeUp}
        >
          <Eyebrow>Para quem é</Eyebrow>
          <h2 className="fsd-display mt-5 text-4xl font-semibold leading-tight sm:text-5xl">
            Do primeiro voo ao operador que quer virar referência.
          </h2>
          <p className="mt-6 max-w-2xl text-lg leading-8 text-[#4c5b50]">
            O Piloto Fronteira foi desenhado para quem quer entrar no mercado com base séria,
            profissionalizar a operação ou transformar conhecimento em fonte de renda no agro.
          </p>
        </motion.div>

        <motion.ul
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={stagger}
          className="grid gap-3"
        >
          {audienceItems.map((item) => (
            <motion.li
              variants={fadeUp}
              key={item}
              className="flex gap-4 rounded-md border border-[#102015]/10 bg-white/72 p-5"
            >
              <BadgeCheck className="mt-0.5 h-5 w-5 shrink-0 text-[#3f7d48]" />
              <span className="leading-7 text-[#334337]">{item}</span>
            </motion.li>
          ))}
        </motion.ul>
      </div>
    </section>
  );
}

function InvestmentSection() {
  return (
    <section
      id="investimento"
      className="bg-[#f8faf7] px-4 py-20 text-[#102015] sm:px-6 lg:px-8 lg:py-28"
    >
      <div className="mx-auto max-w-7xl">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.35 }}
          variants={fadeUp}
          className="max-w-3xl"
        >
          <Eyebrow>Investimento</Eyebrow>
          <h2 className="fsd-display mt-5 text-4xl font-semibold leading-tight sm:text-5xl lg:text-6xl">
            Condições por lote para entrar na próxima turma.
          </h2>
          <p className="mt-6 text-lg leading-8 text-[#4c5b50]">
            O investimento varia conforme o lote. Pagamento à vista em crédito ou pix tem 5% de
            desconto; parcelamento em até 12x, sendo 2x sem juros.
          </p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.18 }}
          variants={stagger}
          className="mt-12 grid gap-4 lg:grid-cols-3"
        >
          {investmentLots.map((lot, index) => (
            <motion.article
              variants={fadeUp}
              key={lot.name}
              className={`rounded-md border p-6 ${
                index === 0
                  ? 'border-[#3f7d48] bg-[#102015] text-white'
                  : 'border-[#102015]/10 bg-white'
              }`}
            >
              <p className={index === 0 ? 'text-[#d8f2a3]' : 'text-[#3f7d48]'}>{lot.note}</p>
              <h3 className="mt-3 text-2xl font-semibold">{lot.name}</h3>
              <p className="fsd-display mt-8 text-4xl font-semibold">{lot.price}</p>
            </motion.article>
          ))}
        </motion.div>

        <div className="mt-8 grid gap-6 lg:grid-cols-[0.9fr_1.1fr]">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.25 }}
            variants={fadeUp}
            className="rounded-md border border-[#102015]/10 bg-white p-6"
          >
            <div className="flex items-center gap-3">
              <CreditCard className="h-6 w-6 text-[#3f7d48]" />
              <h3 className="text-xl font-semibold">O que está incluído</h3>
            </div>
            <ul className="mt-5 grid gap-3">
              {includedItems.map((item) => (
                <li key={item} className="flex gap-3 text-[#4c5b50]">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#3f7d48]" />
                  {item}
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.25 }}
            variants={fadeUp}
            className="rounded-md border border-[#102015]/10 bg-white p-6"
          >
            <div className="flex items-center gap-3">
              <ShieldCheck className="h-6 w-6 text-[#3f7d48]" />
              <h3 className="text-xl font-semibold">Informações importantes</h3>
            </div>
            <ul className="mt-5 grid gap-3">
              {importantNotes.map((item) => (
                <li key={item} className="flex gap-3 text-sm leading-6 text-[#4c5b50]">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#3f7d48]" />
                  {item}
                </li>
              ))}
            </ul>
          </motion.div>
        </div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={fadeUp}
          className="mt-10 rounded-md bg-[#07100c] p-6 text-white sm:p-8 lg:flex lg:items-center lg:justify-between lg:gap-8"
        >
          <div>
            <p className="fsd-display text-3xl font-semibold">
              Fale com a equipe e confirme vagas.
            </p>
            <p className="mt-3 max-w-2xl leading-7 text-white/68">
              A inscrição é conduzida pelo WhatsApp para orientar lote, forma de pagamento e
              detalhes da turma.
            </p>
          </div>
          <WhatsAppCta desktopOnly className="mt-6 w-full lg:mt-0 lg:w-auto">
            Conversar no WhatsApp
          </WhatsAppCta>
        </motion.div>
      </div>
    </section>
  );
}

function FinalSection() {
  return (
    <section className="relative overflow-hidden bg-[#07100c] px-4 py-20 text-white sm:px-6 lg:px-8 lg:py-28">
      <img
        src={p150FieldA}
        alt="Drone agrícola XAG P150 em campo"
        className="absolute inset-0 h-full w-full object-cover opacity-20"
        loading="lazy"
        decoding="async"
      />
      <div className="absolute inset-0 bg-[#07100c]/78" />
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.35 }}
        variants={fadeUp}
        className="relative mx-auto max-w-4xl text-center"
      >
        <Sprout className="mx-auto h-9 w-9 text-[#d8f2a3]" />
        <h2 className="fsd-display mt-6 text-4xl font-semibold leading-tight sm:text-5xl lg:text-6xl">
          A oportunidade não está no drone parado. Está no piloto preparado.
        </h2>
        <p className="mx-auto mt-6 max-w-2xl text-lg leading-8 text-white/72">
          O Piloto Fronteira transforma conteúdo técnico, prática em equipamento agrícola e vivência
          de campo em uma jornada direta para quem quer atuar profissionalmente.
        </p>
        <div className="mt-8 hidden justify-center lg:flex">
          <WhatsAppCta>Quero entrar na próxima turma</WhatsAppCta>
        </div>
      </motion.div>
    </section>
  );
}

function MobileStickyCta() {
  return (
    <div className="fixed bottom-3 left-3 right-3 z-40 lg:hidden">
      <a
        href={whatsappUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="flex min-h-12 items-center justify-center gap-3 rounded-md bg-[#d8f2a3] px-4 text-sm font-semibold text-[#102015] shadow-[0_18px_50px_rgba(2,9,5,0.28)]"
      >
        <WhatsAppIcon className="h-5 w-5" />
        Inscrever pelo WhatsApp
      </a>
    </div>
  );
}

export function PilotoFronteiraLanding() {
  useLenisScroll();

  return (
    <MotionConfig reducedMotion="user">
      <CourseJsonLd />
      <div className="min-h-screen bg-[#07100c] pb-20 lg:pb-0">
        <PilotNav />
        <HeroSection />
        <MarketSection />
        <AuthoritySection />
        <FormationSection />
        <FieldSection />
        <AudienceSection />
        <InvestmentSection />
        <FinalSection />
        <MobileStickyCta />
      </div>
    </MotionConfig>
  );
}
